import {
  createApiClient,
  type ApiClient,
  type ApiError,
  type FetchLike,
} from '@dpem/contracts';

import { currentAccessToken } from '@/lib/auth/session.store';

/**
 * The one api client the app uses.
 *
 * `@dpem/contracts` already generates a typed method per route from the manifest,
 * so there is nothing to write here but the wiring: where the API is, where the
 * token comes from, and what happens on a 401. Adding an endpoint changes the
 * manifest and nothing in `apps/web`.
 *
 * `baseUrl` is empty by default and route paths already start with `/api`, so the
 * browser stays on one origin: no CORS preflight, and the same-origin refresh
 * cookie rides along on every request. In dev, Vite proxies `/api` to the API
 * process (see `vite.config.ts`); in production nginx serves both from one host.
 */

export interface WebApiClientOptions {
  /** Called with the 401 so the session layer can end the session exactly once. */
  readonly onUnauthenticated: (error: ApiError) => void;
  /** Overridden in tests, which supply a fake fetch rather than a server. */
  readonly fetch?: FetchLike;
  readonly baseUrl?: string;
}

const configuredBaseUrl = (): string => {
  const configured = import.meta.env.VITE_API_BASE_URL;
  return typeof configured === 'string' ? configured.replace(/\/$/, '') : '';
};

export const createWebApiClient = (options: WebApiClientOptions): ApiClient =>
  createApiClient({
    baseUrl: options.baseUrl ?? configuredBaseUrl(),
    fetch: options.fetch,
    // Read through the store on every call: a token refreshed while a request
    // was being queued must be the one that goes out.
    getAccessToken: currentAccessToken,
    // Left at the default (true) deliberately. Web and API ship from the same
    // version of `@dpem/contracts`, so a response that fails its own schema is a
    // bug we want loud in development rather than a half-rendered surface.
    validateResponses: true,
    onUnauthenticated: options.onUnauthenticated,
  });
